import { addDays, format, parseISO } from 'date-fns';
import { clampDateRange, daysBetween, getKstToday } from '@/shared/lib';
import { UI_TEXT } from '@/shared/languages';
import type { FilterFieldSpec } from './filter-schema';
import { periodDefaults, type PeriodType } from './period-math';

/**
 * 기간 필터 값(발전량·수익 조회 공용 shape) — 날짜기준(period) + 단일 기준일(date) + range 시작/종료.
 * 각 화면의 Applied 필터가 이를 확장한다(발전량 = +inverterId). 순수 파생·세터·칩 spec 은 여기 SSOT.
 */
export interface PeriodFilterValue {
  /** 날짜기준 — 일/월/연/기간. */
  period: PeriodType;
  /** 단일(일/월/연) 기준값 yyyy-MM-dd. */
  date: string;
  /** range 시작 yyyy-MM-dd. */
  startDate: string;
  /** range 종료 yyyy-MM-dd. */
  endDate: string;
}

/**
 * range 진입 시 **기본 span**(일) — 날짜기준을 '기간'으로 바꾸면 최근 N일이 시드된다(`periodDefaults`).
 *
 * ⚠️ **상한이 아니다.** 예전엔 같은 값이 최대 조회 span 이기도 했는데(레거시 `usePeriodSelector maxRangeDays: 30`),
 * 지금은 기간 길이를 제한하지 않는다 — 유일한 경계는 **종료일 ≤ 오늘**(미래 조회 차단)뿐이다.
 */
export const PERIOD_FILTER_DEFAULT_RANGE_DAYS = 30;

/**
 * 조회 가능 여부 — ADR ⑥ 의 `isApplicable(applied)`. range 만 검사한다(단일 일/월/연은 항상 유효).
 *
 * **`maxDays` 를 주지 않으면 상한이 없다** — 기간 길이 제한은 공용 정책이 아니라 **화면마다 다른 도메인 규칙**이다
 * (BE 응답 크기·차트 포인트 수·업무 관행이 화면별로 다르다). 상한이 필요한 필터가 **자기 상수를 소유**하고
 * 여기 주입한다. 공용 상수를 두면 한 화면 사정으로 바꾼 값이 나머지 화면까지 조용히 끌고 간다.
 *
 * 역전(`시작 > 종료`)은 클램프가 이미 막지만, URL 직접 조작 대비로 여기서도 불가로 본다.
 */
export function isPeriodQueryable(f: PeriodFilterValue, maxDays?: number): boolean {
  if (f.period !== 'range') return true;
  return isRangeQueryable(f.startDate, f.endDate, maxDays);
}

/**
 * 시작~종료 두 값만으로 하는 같은 판정 — **`PeriodFilterValue` shape 이 아닌 화면**(이상알림은 날짜기준 축 없이
 * 범위만 갖는다)이 쓴다. 판정 규칙이 한 곳에 있어야 화면마다 경계가 미세하게 갈리지 않는다.
 */
export function isRangeQueryable(startDate: string, endDate: string, maxDays?: number): boolean {
  if (startDate > endDate) return false;
  return maxDays == null || daysBetween(startDate, endDate) <= maxDays;
}

/** yyyy-MM-dd 를 days 만큼 이동(입력 min/max·span 클램프 공용). */
export function shiftDate(ymd: string, days: number): string {
  return format(addDays(parseISO(ymd), days), 'yyyy-MM-dd');
}

/**
 * 기간(range) 클램프 — 역전 방지(clampDateRange) + **미래 차단**. 방금 바꾼 쪽(changed)을 존중하고 반대쪽을
 * 이동해 항상 유효 상태(시작 ≤ 종료 ≤ 오늘)를 유지한다. 입력 min/max 가 1차 차단, 이 클램프가 backstop.
 *
 * **span 상한은 없다** — 기간 길이는 제한하지 않는다(`PERIOD_FILTER_DEFAULT_RANGE_DAYS` 는 기본값일 뿐 경계가 아니다).
 */
export function clampPeriodRange(
  start: string,
  end: string,
  changed: 'start' | 'end',
): { start: string; end: string } {
  const { start: s, end: e } = clampDateRange(start, end, changed); // 역전 우선
  const today = getKstToday();
  // 종료가 오늘을 넘으면 오늘로. 그 결과 시작이 종료를 앞지르면 시작도 함께 당긴다(역전 재발 방지).
  const end2 = e > today ? today : e;
  return { start: s > end2 ? end2 : s, end: end2 };
}

/**
 * 기간 세터 factory — 발전량·수익 **live** 상태 훅 공유. `setFilters`(functional update)로 write. 날짜기준 변경은
 * 기간(date/start/end)을 단위 기본값으로 cascade 리셋, range 는 clampPeriodRange 로 역전·미래 차단.
 */
export function periodSetters<T extends PeriodFilterValue>(
  setFilters: (updater: (prev: T) => T) => void,
) {
  return {
    setPeriod: (period: PeriodType) =>
      setFilters((prev) => ({
        ...prev,
        period,
        ...periodDefaults(period, PERIOD_FILTER_DEFAULT_RANGE_DAYS),
      })),
    setDate: (date: string) => setFilters((prev) => ({ ...prev, date })),
    setStartDate: (startDate: string) =>
      setFilters((prev) => {
        const { start, end } = clampPeriodRange(startDate, prev.endDate, 'start');
        return { ...prev, startDate: start, endDate: end };
      }),
    setEndDate: (endDate: string) =>
      setFilters((prev) => {
        const { start, end } = clampPeriodRange(prev.startDate, endDate, 'end');
        return { ...prev, startDate: start, endDate: end };
      }),
  };
}

/** 조회기간 칩 값 — 날짜기준별(일=yyyy-MM-dd·월=yyyy-MM·연=yyyy·기간=시작~종료). */
function formatPeriodChip(f: PeriodFilterValue): string {
  switch (f.period) {
    case 'day':
      return f.date;
    case 'month':
      return f.date.slice(0, 7);
    case 'year':
      return f.date.slice(0, 4);
    case 'range':
      return `${f.startDate} ~ ${f.endDate}`;
  }
}

/**
 * 조회조건 기간 칩(발전량·수익 공용) — **값 하나짜리 칩 한 개**(`기간: 2026-06`). 배지 미집계
 * (admin 조회기간·상태 칩 정합). 화면별 추가 차원(예: 발전량 인버터)은 각 스키마가 concat.
 *
 * 날짜기준(일/월/연/기간)은 **칩을 따로 세우지 않는다** — 값 포맷이 곧 단위라(yyyy-MM-dd / yyyy-MM / yyyy /
 * 시작~종료) 기준 칩이 더하는 정보가 없다. 편집 진입점은 잃지 않는다: `dateType` 컨트롤이 같은
 * `PERIOD_GROUP` 이라 이 칩의 팝오버에 함께 뜨고, 드로워 스택엔 늘 제 자리로 있다.
 * 차원명이 기준별로 갈리지 않으므로(옛 `조회월`·`조회연도`) 라벨은 `PERIOD_FILTER_QUERY_PERIOD` 고정.
 */
export function periodChipSpecs<T extends PeriodFilterValue>(): FilterFieldSpec<T>[] {
  return [
    {
      key: 'period',
      label: UI_TEXT.PERIOD_FILTER_QUERY_PERIOD,
      chip: (f) => ({
        label: `${UI_TEXT.PERIOD_FILTER_QUERY_PERIOD}: ${formatPeriodChip(f)}`,
      }),
    },
  ];
}
